'use client';

import lottie, { type AnimationItem } from 'lottie-web';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../../lib/cn';
import agentTeamsIconLottie from '../../assets/agentteams-icon-lottie.json';

type AgentTeamsLottieLoaderProps = {
  animate?: boolean;
  className?: string;
  decorative?: boolean;
  label?: string;
  /** 애니메이션이 한 사이클(루프)을 마칠 때마다 호출된다. */
  onCycleComplete?: () => void;
};

type ThemeName = 'winter' | 'night';
type LottieShapeItem = {
  ty?: string;
  nm?: string;
  c?: { k?: number[] };
  o?: { k?: number };
  it?: LottieShapeItem[];
};
type LottieLayer = {
  shapes?: LottieShapeItem[];
};
type LottieData = {
  layers?: LottieLayer[];
};

const cloneLottieData = (): LottieData => JSON.parse(JSON.stringify(agentTeamsIconLottie)) as LottieData;

const getCurrentTheme = (): ThemeName =>
  document.documentElement.getAttribute('data-theme') === 'night' ? 'night' : 'winter';

const applyLoaderColors = (data: LottieData, theme: ThemeName): LottieData => {
  const symbolColor = theme === 'night' ? [0.231, 0.51, 0.965, 1] : [0.118, 0.161, 0.231, 1];
  const symbolOpacity = theme === 'night' ? 56 : 62;
  const backgroundColor = theme === 'night' ? [0.231, 0.51, 0.965, 1] : [0.576, 0.631, 0.722, 1];
  const backgroundOpacity = theme === 'night' ? 6 : 5;

  const applyShapeColors = (items: LottieShapeItem[] = []) => {
    for (const item of items) {
      if ((item.ty === 'fl' || item.ty === 'st') && item.c?.k && item.o) {
        const isBackground = item.nm === 'blue fill';
        item.c.k = isBackground ? backgroundColor : symbolColor;
        item.o.k = isBackground ? backgroundOpacity : symbolOpacity;
      }

      if (item.it) {
        applyShapeColors(item.it);
      }
    }
  };

  for (const layer of data.layers ?? []) {
    applyShapeColors(layer.shapes);
  }

  return data;
};

export const AgentTeamsLottieLoader = ({
  animate = true,
  className,
  decorative = false,
  label,
  onCycleComplete,
}: AgentTeamsLottieLoaderProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [theme, setTheme] = useState<ThemeName>('winter');
  const animationData = useMemo(() => applyLoaderColors(cloneLottieData(), theme), [theme]);

  const onCycleCompleteRef = useRef(onCycleComplete);
  useEffect(() => {
    onCycleCompleteRef.current = onCycleComplete;
  }, [onCycleComplete]);

  useEffect(() => {
    const updateTheme = () => {
      setTheme(getCurrentTheme());
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const animation: AnimationItem = lottie.loadAnimation({
      animationData,
      autoplay: animate,
      container: containerRef.current,
      loop: animate,
      renderer: 'svg',
      rendererSettings: {
        progressiveLoad: true,
      },
    });

    const showStaticFrame = () => {
      animation.goToAndStop(Math.max(animation.totalFrames - 1, 0), true);
    };
    const handleLoopComplete = () => onCycleCompleteRef.current?.();
    if (animate) {
      animation.addEventListener('loopComplete', handleLoopComplete);
    } else {
      animation.addEventListener('DOMLoaded', showStaticFrame);
    }

    return () => {
      if (animate) {
        animation.removeEventListener('loopComplete', handleLoopComplete);
      } else {
        animation.removeEventListener('DOMLoaded', showStaticFrame);
      }
      animation.destroy();
    };
  }, [animate, animationData]);

  return (
    <div
      ref={containerRef}
      role={decorative ? undefined : 'status'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : label}
      className={cn('h-20 w-20', className)}
    />
  );
};
